import { Icon } from '@iconify/react'
import type { MascotGamePayload, Room, SendGameAction } from './mascot-game-types'
import { dailyResetIn, gameDay, projectNeeds, timeLabel } from './mascot-game-view'
import {
  SLEEP_DAY_MINUTES,
  SLEEP_NIGHT_MINUTES,
  sleepSecondsUntilFull,
} from './mascot-sleep'
import { MascotVoice } from './MascotVoice'
import { ActionCountdown } from './ActionCountdown'
import { MascotCarePurchaseButton } from './MascotCarePurchase'
import {
  hasWaitPass,
  type MascotCareBenefit,
  type MascotWaitAction,
} from './mascot-care-options'
import styles from './MascotGame.module.css'

export function MascotRoomPanel({
  data,
  room,
  now,
  disabled,
  send,
  onShop,
  onCustomize,
  onRoom,
  onPurchase,
  mascotArtwork,
}: {
  data: MascotGamePayload
  room: Room
  now: number
  disabled: boolean
  send: SendGameAction
  onShop: () => void
  onCustomize: () => void
  onRoom: (room: Room) => void
  onPurchase?: (benefit: MascotCareBenefit) => void
  mascotArtwork?: import('react').ReactNode
}) {
  const { state, catalog } = data
  const jungle = catalog.habitat === 'jungle'
  const sheep = catalog.habitat === 'farm'
  const sleeping = state.sleeping
  const needs = projectNeeds(state, now)
  const cooldown = (action: MascotWaitAction) =>
    hasWaitPass(state, action) ? 0 : Math.max(0, (state.cooldowns[action] ?? 0) - now)
  const blocked = disabled || sleeping
  const giftClaimed = state.last_gift === gameDay(now) && !hasWaitPass(state, 'daily')
  const harvestReady =
    hasWaitPass(state, 'harvest') || now >= (state.garden?.ready_at ?? Infinity)
  const actionWait = (action: Parameters<SendGameAction>[0]) => {
    if (action.action === 'pet') return 0
    const key =
      action.action === 'wash' && action.item === 'water'
        ? 'water'
        : (action.action as MascotWaitAction)
    return action.action === 'daily' && giftClaimed
      ? dailyResetIn(now)
      : action.action === 'harvest'
        ? hasWaitPass(state, 'harvest')
          ? 0
          : Math.max(0, (state.garden?.ready_at ?? 0) - now)
        : cooldown(key)
  }
  const careFeedback = (action: Parameters<SendGameAction>[0]) => {
    if (action.action === 'pet') return ''
    const remaining = actionWait(action)
    if (remaining > 0) return `Disponível em ${timeLabel(remaining)}`
    if (sleeping && ['pet', 'feed', 'wash', 'heal', 'snack'].includes(action.action))
      return 'Acorde o mascote primeiro'
    if (action.action === 'feed' && needs.satiety >= 95)
      return 'Barriguinha já está cheia'
    if (action.action === 'snack' && state.version === 2 && needs.satiety >= 45)
      return 'Libera abaixo de 45 de saciedade'
    if (action.action === 'wash' && needs.cleanliness >= 100)
      return 'Higiene já está em dia'
    if (
      action.action === 'heal' &&
      needs[action.item === 'energy' ? 'energy' : 'health'] >= 95
    )
      return action.item === 'energy' ? 'Energia já está cheia' : 'Saúde já está em dia'
    if (
      action.item &&
      action.item !== 'water' &&
      ['feed', 'wash', 'heal'].includes(action.action) &&
      !(state.inventory[action.item] > 0)
    )
      return 'Item esgotado · reponha na lojinha'
    if (action.action === 'plant' && !state.inventory.seed)
      return state.level < (catalog.items.seed?.level ?? 4)
        ? `Sementes liberam no nível ${catalog.items.seed?.level ?? 4}`
        : 'Sem sementes · visite a lojinha'
    if (
      action.action === 'harvest' &&
      (state.inventory[sheep ? 'hay' : jungle ? 'meat' : 'apple'] ?? 0) > 96
    )
      return sheep
        ? 'Use algumas porções de feno para liberar espaço'
        : jungle
          ? 'Use algumas porções de carne para liberar espaço'
          : 'Use algumas maçãs para liberar espaço'
    if (action.action === 'daily' && giftClaimed)
      return `Novo presente em ${timeLabel(dailyResetIn(now))}`
    return ''
  }
  const button = (
    label: string,
    icon: string,
    action: Parameters<SendGameAction>[0],
    inactive = false,
    primary = false
  ) => {
    const feedback = careFeedback(action)
    const remaining = actionWait(action)
    return (
      <div className={styles.careAction}>
        <button
          className={`${primary ? styles.primaryButton : styles.secondaryButton} ${styles.actionCard}`}
          data-action={action.action}
          data-waiting={remaining > 0}
          onClick={() => void send(action)}
          disabled={action.action !== 'pet' && (disabled || inactive || !!feedback)}
        >
          <span className={styles.actionArt} aria-hidden="true">
            {action.item && catalog.items[action.item]?.icon
              ? catalog.items[action.item].icon
              : ((
                  {
                    pet: '💛',
                    daily: '🎁',
                    snack: sheep ? '🌾' : jungle ? '🥩' : '🍎',
                    wash: '🚿',
                    sleep: '🌙',
                    wake: '☀️',
                    plant: '🌱',
                    harvest: '🧺',
                  } as Record<string, string>
                )[action.action] ?? <Icon icon={icon} />)}
          </span>
          <span className={styles.actionCopy}>
            <span>{label}</span>
            {feedback && remaining <= 0 && (
              <small className={styles.actionHint}>{feedback}</small>
            )}
          </span>
          {remaining > 0 && (
            <ActionCountdown
              seconds={remaining}
              label={action.action === 'daily' ? 'Novo presente em' : undefined}
            />
          )}
        </button>
        {remaining > 0 && (
          <MascotCarePurchaseButton
            benefit={
              `wait_${action.action === 'wash' && action.item === 'water' ? 'water' : action.action}` as MascotCareBenefit
            }
            disabled={disabled}
            onPurchase={onPurchase}
          />
        )}
      </div>
    )
  }
  return (
    <aside className={styles.roomPanel} data-room={room}>
      {sleeping && room !== 'bedroom' && (
        <div className={styles.sleepNotice}>
          <Icon icon="mdi:weather-night" />
          <span>Seu companheiro está descansando.</span>
          {button('Acordar', 'mdi:weather-sunny', { action: 'wake' })}
        </div>
      )}
      {room === 'home' && (
        <>
          <span className={styles.eyebrow}>BOM TER VOCÊ AQUI</span>
          <h3>
            Pequenos gestos.
            <br />
            Uma grande amizade.
          </h3>
          <p>Cuide, descubra e brinque. Seu cantinho continua aqui quando você voltar.</p>
          {button(
            'Fazer um carinho',
            'mdi:heart-outline',
            { action: 'pet' },
            false,
            true
          )}
          <p className={styles.caption}>
            Carinho à vontade. As recompensas têm intervalo de 10 minutos.
          </p>
          {cooldown('pet') > 0 && (state.daily.pets ?? 0) < 3 && (
            <MascotCarePurchaseButton
              benefit="wait_pet"
              disabled={disabled}
              onPurchase={onPurchase}
            />
          )}
          {button(
            giftClaimed ? 'Presente recebido' : 'Abrir meu presente',
            'mdi:gift-outline',
            { action: 'daily' },
            giftClaimed
          )}
          <p className={styles.caption}>
            {giftClaimed
              ? 'Presente de hoje recebido. Amanhã tem mais!'
              : `Presente do dia: ${catalog.rules?.gift_min ?? 8} a ${catalog.rules?.gift_max ?? 18} moedas.`}
          </p>
          <MascotVoice />
        </>
      )}
      {room === 'kitchen' && (
        <>
          <span className={styles.eyebrow}>SUA DESPENSA</span>
          <h3>Receita de felicidade.</h3>
          <p>
            Toque em uma comidinha para servir. Os itens da despensa são consumidos a cada
            refeição.
          </p>
          <div className={styles.inventory}>
            {Object.entries(catalog.items)
              .filter(([, item]) => item.kind === 'food')
              .map(([id, item]) => (
                <button
                  key={id}
                  className={styles.inventoryItem}
                  data-waiting={cooldown('feed') > 0}
                  disabled={blocked || !!careFeedback({ action: 'feed', item: id })}
                  onClick={() => void send({ action: 'feed', item: id })}
                >
                  <span className={styles.itemEmoji}>{item.icon}</span>
                  <span>
                    <strong>{item.name}</strong>
                    <small>{item.description}</small>
                    {cooldown('feed') <= 0 &&
                      careFeedback({ action: 'feed', item: id }) && (
                        <small className={styles.actionHint}>
                          {careFeedback({ action: 'feed', item: id })}
                        </small>
                      )}
                  </span>
                  <b>×{state.inventory[id] ?? 0}</b>
                  {cooldown('feed') > 0 && <ActionCountdown seconds={cooldown('feed')} />}
                </button>
              ))}
          </div>
          {cooldown('feed') > 0 && needs.satiety < 95 && (
            <MascotCarePurchaseButton
              benefit="wait_feed"
              disabled={disabled}
              onPurchase={onPurchase}
            />
          )}
          {needs.satiety < 100 && (
            <MascotCarePurchaseButton
              benefit="satiety"
              disabled={disabled}
              onPurchase={onPurchase}
            />
          )}
          {button(
            `${sheep ? 'Feno grátis' : jungle ? 'Carne grátis' : 'Lanchinho grátis'} · +${state.version === 2 ? 10 : 12}`,
            'mdi:food-apple-outline',
            { action: 'snack' },
            sleeping ||
              cooldown('snack') > 0 ||
              (state.version === 2 && needs.satiety >= 45)
          )}
          <p className={styles.caption}>
            O lanchinho gratuito ajuda quando a saciedade está abaixo de 45, uma vez por
            hora.
          </p>
          <button className={styles.textButton} onClick={onShop}>
            <Icon icon="mdi:store-outline" />
            Repor a despensa
          </button>
        </>
      )}
      {room === 'bath' && (
        <>
          <span className={styles.eyebrow}>BANHO & BEM-ESTAR</span>
          <h3>Um banho de carinho.</h3>
          <p>
            A ducha gratuita e o banho de espuma restauram 100% de higiene. A ducha fica
            disponível a cada 30 minutos. Seu mascote se suja ao jogar.
          </p>
          {button(
            'Dar uma ducha · 100% de higiene',
            'mdi:shower-head',
            { action: 'wash', item: 'water' },
            blocked || cooldown('water') > 0,
            true
          )}
          {button(
            `Banho de espuma · ${state.inventory.soap ?? 0} sabonetes`,
            'mdi:bathtub-outline',
            { action: 'wash', item: 'soap' },
            blocked || cooldown('wash') > 0 || !state.inventory.soap
          )}
          <div className={styles.panelDivider} />
          <span className={styles.eyebrow}>FARMACINHA</span>
          <button className={styles.primaryButton} onClick={() => onRoom('veterinary')}>
            <Icon icon="mdi:medical-bag" /> Levar ao veterinário
          </button>
          <p className={styles.caption}>
            A consulta com remédio é uma compra que apoia a entidade desta campanha e
            restaura a saúde após o pagamento.
          </p>
          {button(
            `${catalog.items.energy?.name ?? 'Suco de energia'} · ${state.inventory.energy ?? 0} disponíveis`,
            'mdi:cup-outline',
            { action: 'heal', item: 'energy' },
            blocked || cooldown('heal') > 0 || !state.inventory.energy
          )}
          {needs.energy < 100 && (
            <MascotCarePurchaseButton
              benefit="energy"
              disabled={disabled}
              onPurchase={onPurchase}
            />
          )}
          {cooldown('wash') > 0 && (
            <p className={styles.caption}>
              Secando o pelo… {timeLabel(cooldown('wash'))}.
            </p>
          )}
          <button className={styles.textButton} onClick={onShop}>
            Visitar a lojinha <Icon icon="mdi:arrow-right" />
          </button>
        </>
      )}
      {room === 'bedroom' && (
        <>
          <span className={styles.eyebrow}>UM DESCANSO MERECIDO</span>
          <h3>{sleeping ? 'Recarregando nos sonhos.' : 'Pausa para sonhar.'}</h3>
          <p>
            Das 8h às 20h, uma carga completa de energia leva{' '}
            {catalog.rules?.sleep_day_minutes ?? SLEEP_DAY_MINUTES} minutos de sono. Das
            20h às 8h, leva{' '}
            {(catalog.rules?.sleep_night_minutes ?? SLEEP_NIGHT_MINUTES) / 60} horas.
            Horário de Brasília. O tempo é proporcional à energia que falta e o ritmo muda
            às 8h e às 20h, mesmo com o jogo fechado.
          </p>
          {button(
            jungle
              ? sleeping
                ? 'Acordar na selva'
                : 'Descansar no abrigo'
              : sleeping
                ? 'Acender a luz e acordar'
                : 'Apagar a luz e dormir',
            sleeping ? 'mdi:lightbulb-on-outline' : 'mdi:weather-night',
            { action: sleeping ? 'wake' : 'sleep' },
            false,
            true
          )}
          <div className={styles.restCard}>
            <span className={styles.actionArt} aria-hidden="true">
              ⚡
            </span>
            <strong>{Math.round(needs.energy)}% de energia</strong>
            <progress value={needs.energy} max={100} aria-label="Energia recuperada" />
            <small>
              {needs.energy >= 100
                ? 'Energia completa!'
                : sleeping
                  ? `Completa em ${timeLabel(sleepSecondsUntilFull(needs.energy, now))}`
                  : 'Coloque para dormir e recuperar energia'}
            </small>
          </div>
        </>
      )}
      {room === 'garden' && (
        <>
          <span className={styles.eyebrow}>
            {sheep
              ? 'PASTAGEM DA OVELHINHA'
              : jungle
                ? 'CUIDE DA MATA'
                : 'DA SEMENTE AO LANCHINHO'}
          </span>
          <h3>O tempo faz crescer.</h3>
          <p>
            {sheep
              ? 'Semeie a pastagem e colha três porções de feno em '
              : jungle
                ? 'Plante uma muda para restaurar a floresta. O cuidador entrega três porções de carne em '
                : 'Plante uma semente e colha três maçãs em '}
            {catalog.rules
              ? `${catalog.rules.garden_seconds / 3600} horas`
              : '30 minutos'}
            .
          </p>
          <div className={styles.gardenPot}>
            <span>
              {state.garden ? (harvestReady ? (sheep ? '🌾' : '🌳') : '🌱') : '🪴'}
            </span>
            <strong>
              {state.garden
                ? harvestReady
                  ? jungle
                    ? 'Mata restaurada!'
                    : 'Hora de colher!'
                  : timeLabel(state.garden.ready_at - now)
                : 'Um novo começo'}
            </strong>
            <small>
              {state.garden
                ? jungle
                  ? 'Sua muda cresce mesmo com o jogo fechado.'
                  : 'Seu jardim cresce mesmo com o jogo fechado.'
                : `${state.inventory.seed ?? 0} sementes na sua mochila`}
            </small>
          </div>
          {state.garden
            ? button(
                sheep
                  ? 'Colher 3 porções de feno'
                  : jungle
                    ? 'Receber 3 porções de carne'
                    : 'Colher 3 maçãs',
                'mdi:basket-outline',
                { action: 'harvest' },
                now < state.garden.ready_at && !hasWaitPass(state, 'harvest'),
                true
              )
            : button(
                'Plantar uma semente',
                'mdi:sprout-outline',
                { action: 'plant' },
                !state.inventory.seed,
                true
              )}
          <button className={styles.textButton} onClick={onShop}>
            Comprar sementes <Icon icon="mdi:arrow-right" />
          </button>
        </>
      )}
      {room === 'wardrobe' && (
        <>
          <span className={styles.eyebrow}>PERSONALIDADE DE SOBRA</span>
          <h3>Quem você vai ser hoje?</h3>
          <p>Mude o nome, a pelagem e os acessórios do seu companheiro.</p>
          <button
            className={`${styles.primaryButton} ${styles.actionCard}`}
            onClick={onCustomize}
            disabled={disabled}
          >
            <span className={styles.actionArt} aria-hidden="true">
              {mascotArtwork ?? '🐾'}
            </span>
            Personalizar mascote
          </button>
          <div className={styles.panelDivider} />
          <span className={styles.eyebrow}>MINHAS DECORAÇÕES</span>
          <div className={styles.decorChoices}>
            {state.owned_decor.map((id) => (
              <button
                key={id}
                aria-pressed={state.decor === id}
                disabled={disabled}
                onClick={() => void send({ action: 'decorate', item: id })}
              >
                <span>{catalog.items[id]?.icon}</span>
                {catalog.items[id]?.name}
                <Icon
                  icon={state.decor === id ? 'mdi:check-circle' : 'mdi:circle-outline'}
                />
              </button>
            ))}
          </div>
          <button className={styles.textButton} onClick={onShop}>
            Descobrir novas decorações <Icon icon="mdi:arrow-right" />
          </button>
          <p className={styles.caption}>
            {sheep
              ? 'Sua ovelhinha vive nas campanhas da Agroplay. Moedas, inventário e progresso ficam preservados na sua conta.'
              : 'Seu mascote, sua casa, moedas e cuidados acompanham sua conta na Sortee em todas as campanhas.'}
          </p>
        </>
      )}
      {room === 'home' && (
        <button className={styles.textButton} onClick={() => onRoom('journal')}>
          Ver a missão de hoje <Icon icon="mdi:arrow-right" />
        </button>
      )}
    </aside>
  )
}
